/**
 * terrainFactors.js
 * PSP/CRE Tool v2.1 - Phase 3 Distance Configuration
 * 
 * Terrain modifiers affecting evacuation time and route planning.
 * Based on NATO doctrine and Ukrainian 2022-2024 lessons learned.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 27, 2024
 * Version: 1.2.0 - V4-ARCH-015 (April 8, 2026): Added URBAN_COMBAT terrain type.
 *   time_modifier=2.0, speed_modifier=0.50, max_speed_kmh=15, helicopter_factor=0.2.
 *   Values sourced from Ukraine 2022-2024 data (Mariupol, Bakhmut). Completes 10-terrain
 *   canonical set. URBAN_COMBAT was in evacuationRoutes.js but absent from this canonical source.
 * Version: 1.1.0 - MED-02/04 + SVM-03 aliases (February 24, 2026)
 *
 * Changes in v1.1.0 (February 24, 2026):
 * - MED-02/04: Added `speed_modifier` and `availability_modifier` alias fields to
 *   all 9 TERRAIN_TYPES entries (PLAINS, ROLLING_HILLS, MOUNTAIN, URBAN, FOREST,
 *   SWAMP_WETLAND, DESERT, COASTAL, ARCTIC). medevacModule.js v5.2.0 now uses these
 *   alias fields instead of its local TERRAIN_GROUND_MODIFIERS block. Canonical key
 *   adapter (OPEN→PLAINS, MARSH→SWAMP_WETLAND, RIVER→SWAMP_WETLAND, MIXED→ROLLING_HILLS)
 *   lives in medevacModule.js.
 * - SVM-03: TERRAIN_TYPES already usable for SVM-03 via operationsConfig.js (which
 *   provides TERRAIN_TYPES with .medical.evacuation_speed field). survivalModule.js
 *   SVM-03 uses operationsConfig TERRAIN_TYPES. terrainFactors.js TERRAIN_TYPES is
 *   the richer canonical; both files coexist.
 * Version: 1.0.0
 * 
 * USAGE:
 *   import { TERRAIN_TYPES, getTerrainFactor, calculateEffectiveDistance } from './terrainFactors';
 */

// ============================================================================
// TERRAIN TYPE DEFINITIONS
// ============================================================================

/**
 * Terrain types with time and distance modifiers
 * 
 * time_modifier: Multiplier for travel time (>1 = slower)
 * distance_modifier: Effective distance multiplier for fuel/wear calculations
 * helicopter_factor: Modifier for helicopter operations (weather, landing zones)
 * max_speed_kmh: Maximum safe convoy speed
 * night_penalty: Additional modifier for night operations
 */
export const TERRAIN_TYPES = {
  PLAINS: {
    value: 'PLAINS',
    label: 'Plains/Flatland',
    description: 'Open terrain with good road networks',
    icon: '🌾',
    
    time_modifier: 1.0,           // Baseline
    distance_modifier: 1.0,       // Baseline
    helicopter_factor: 1.0,       // Full capability
    max_speed_kmh: 60,            // Good roads allow higher speeds
    night_penalty: 1.1,           // Minimal night impact
    
    // MED-02/04: alias fields for medevacModule (terrainFactors.js v1.1)
    speed_modifier: 1.00,         // 1/time_modifier — ground evac speed factor
    availability_modifier: 1.00,  // Vehicle availability in this terrain (no obstacles)
    
    typical_regions: ['Baragan Plain', 'Hungarian Plain', 'Northern Germany'],
    
    medical_considerations: {
      dust_dnbi_factor: 1.1,      // Dust increases respiratory DNBI
      heatstroke_risk: 'HIGH',    // Open terrain, sun exposure
      hypothermia_risk: 'MEDIUM', // Less shelter from wind
    },
    
    educational_note: 'Best terrain for ground evacuation. Helicopter LZs easy to establish.'
  },
  
  ROLLING_HILLS: {
    value: 'ROLLING_HILLS',
    label: 'Rolling Hills',
    description: 'Moderate elevation changes, mixed road quality',
    icon: '⛰️',
    
    time_modifier: 1.15,
    distance_modifier: 1.1,
    helicopter_factor: 1.0,
    max_speed_kmh: 50,
    night_penalty: 1.2,
    
    speed_modifier: 0.87,         // 1/1.15 — moderate terrain slows vehicles
    availability_modifier: 0.90,  // Minor route restrictions
    
    typical_regions: ['Transylvanian Plateau', 'Moldavian Hills', 'Central Europe'],
    
    medical_considerations: {
      dust_dnbi_factor: 1.0,
      heatstroke_risk: 'MEDIUM',
      hypothermia_risk: 'MEDIUM',
    },
    
    educational_note: 'Common terrain in Eastern Europe. Plan for winding roads.'
  },
  
  MOUNTAIN: {
    value: 'MOUNTAIN',
    label: 'Mountainous',
    description: 'High elevation, limited routes, weather-dependent',
    icon: '🏔️',
    
    time_modifier: 1.5,           // 50% slower
    distance_modifier: 1.4,       // Winding roads add distance
    helicopter_factor: 0.7,       // Weather limitations, altitude
    max_speed_kmh: 35,            // Mountain roads require slow speeds
    night_penalty: 1.5,           // Dangerous at night
    
    speed_modifier: 0.67,         // 1/1.5 — severe speed reduction
    availability_modifier: 0.70,  // Many vehicles unable to operate
    
    typical_regions: ['Carpathian Mountains', 'Alps', 'Balkans'],
    
    medical_considerations: {
      dust_dnbi_factor: 0.9,      // Less dust
      heatstroke_risk: 'LOW',
      hypothermia_risk: 'VERY_HIGH', // Altitude + exposure
      altitude_sickness: true,
    },
    
    educational_note: 'Evacuation significantly impaired. Pre-position assets forward. Helicopter ops weather-dependent.'
  },
  
  URBAN: {
    value: 'URBAN',
    label: 'Urban/City',
    description: 'Dense infrastructure, traffic, potential rubble',
    icon: '🏙️',
    
    time_modifier: 1.3,           // Traffic, detours
    distance_modifier: 1.2,       // Route restrictions
    helicopter_factor: 0.8,       // Limited LZs, wires, buildings
    max_speed_kmh: 30,            // Safety in built-up areas
    night_penalty: 1.1,           // Street lights help
    
    speed_modifier: 0.77,         // 1/1.3 — traffic, detours, checkpoints
    availability_modifier: 0.90,  // Most vehicles usable but LZs limited
    
    typical_regions: ['Any major city', 'Industrial zones'],
    
    medical_considerations: {
      dust_dnbi_factor: 0.8,      // Less dust
      heatstroke_risk: 'MEDIUM',
      hypothermia_risk: 'LOW',    // Shelter available
      blast_injury_modifier: 1.3, // Buildings amplify blast
      collapse_injury_risk: 'HIGH',
    },
    
    ukraine_lesson: 'Mariupol, Bakhmut showed urban evacuation extremely difficult. Plan multiple routes.',
    
    educational_note: 'Contested urban areas may require armored evacuation. HLZs must be secured.'
  },

  // V4-ARCH-015: URBAN_COMBAT — active urban combat zone. Exists in evacuationRoutes.js (factor=2.0)
  // but was absent from this canonical source. Added here to complete the 10-terrain canonical set.
  // Values: Ukraine 2022-2024 (Mariupol, Bakhmut, Kherson). Factor 2.0 = evacuation often doubled
  // or impossible for hours. Armored vehicles mandatory; helicopter approach life-threatening.
  URBAN_COMBAT: {
    value: 'URBAN_COMBAT',
    label: 'Urban Combat Zone',
    description: 'Active urban combat with route denial, IEDs, sniper threat',

    time_modifier: 2.0,           // Evacuation often doubles or is impossible for periods
    distance_modifier: 1.8,       // Rubble, route denial, mandatory detours
    helicopter_factor: 0.2,       // Extreme risk: ground fire, IEDs, no secured HLZs
    max_speed_kmh: 15,            // Armored vehicles only; stop-and-go movement
    night_penalty: 1.3,           // Thermal advantage for defenders; curfews

    speed_modifier: 0.50,         // 1/2.0 — route denial, rubble, threat halves effective speed
    availability_modifier: 0.50,  // Only armored/protected vehicles viable; wheeled very high risk

    typical_regions: ['Mariupol 2022', 'Bakhmut 2022-2023', 'Kherson 2022', 'Fallujah 2004'],

    medical_considerations: {
      dust_dnbi_factor: 0.9,
      heatstroke_risk: 'MEDIUM',
      hypothermia_risk: 'MEDIUM',
      blast_injury_modifier: 1.8, // Confined spaces amplify blast; building collapse adds crush
      collapse_injury_risk: 'VERY_HIGH',
      secondary_casualty_risk: 'HIGH', // Evacuation teams themselves at high risk
    },

    ukraine_lesson: 'Mariupol: evacuation corridors failed repeatedly. Wounded held in basements for days. ' +
                    'Bakhmut: avg evacuation time >4 hours in active contact. Armored MTLBs only viable vehicle.',

    educational_note: 'Pre-position Role 1 assets forward (within the perimeter). ' +
                      'Ground evacuation requires armored vehicle and armed escort. ' +
                      'Helicopter approach only under air superiority and secured HLZ. ' +
                      'Plan for 2-4x normal evacuation times — golden hour rarely achievable.'
  },

  FOREST: {
    value: 'FOREST',
    label: 'Forested',
    description: 'Dense vegetation, limited visibility and LZs',
    icon: '🌲',
    
    time_modifier: 1.25,
    distance_modifier: 1.15,
    helicopter_factor: 0.6,       // Very limited LZs
    max_speed_kmh: 40,
    night_penalty: 1.4,           // Very dark
    
    speed_modifier: 0.80,         // 1/1.25 — off-road movement limited
    availability_modifier: 0.85,  // Most vehicles usable, LZs rare
    
    typical_regions: ['Romanian Carpathians', 'Belarusian forests', 'Scandinavia'],
    
    medical_considerations: {
      dust_dnbi_factor: 0.7,
      heatstroke_risk: 'LOW',
      hypothermia_risk: 'HIGH',   // Wet conditions
      tick_borne_disease: true,
    },
    
    educational_note: 'Helicopter LZs must be pre-cut or use natural clearings. Ground evac preferred.'
  },
  
  SWAMP_WETLAND: {
    value: 'SWAMP_WETLAND',
    label: 'Swamp/Wetland',
    description: 'Limited trafficability, seasonal flooding',
    icon: '🌿',
    
    time_modifier: 1.8,           // Very slow
    distance_modifier: 1.6,       // Major detours required
    helicopter_factor: 0.5,       // Soft ground limits LZs
    max_speed_kmh: 25,
    night_penalty: 1.6,
    
    speed_modifier: 0.55,         // 1/1.8 — only tracked vehicles viable
    availability_modifier: 0.60,  // Many vehicle types non-trafficable
    
    typical_regions: ['Danube Delta', 'Pripyat Marshes', 'Baltic wetlands'],
    
    medical_considerations: {
      dust_dnbi_factor: 0.5,
      heatstroke_risk: 'MEDIUM',
      hypothermia_risk: 'VERY_HIGH', // Wet + cold
      waterborne_disease: true,
      trench_foot_risk: 'VERY_HIGH',
    },
    
    educational_note: 'Ground evacuation may be impossible. Boat or helicopter only in many cases.'
  },
  
  DESERT: {
    value: 'DESERT',
    label: 'Desert/Arid',
    description: 'Extreme temperatures, sand, limited landmarks',
    icon: '🏜️',
    
    time_modifier: 1.2,
    distance_modifier: 1.1,
    helicopter_factor: 0.9,       // Good except for sandstorms
    max_speed_kmh: 50,
    night_penalty: 1.3,           // Navigation difficult
    
    speed_modifier: 0.85,         // Soft sand restricts wheeled vehicles
    availability_modifier: 0.80,  // Risk of bogging, heat damage to vehicles
    
    typical_regions: ['Middle East', 'North Africa', 'Central Asia'],
    
    medical_considerations: {
      dust_dnbi_factor: 1.5,      // Major dust issue
      heatstroke_risk: 'EXTREME',
      hypothermia_risk: 'HIGH',   // Desert nights are cold
      dehydration_multiplier: 2.0,
    },
    
    educational_note: 'Heat casualties may exceed combat casualties. Hydration critical.'
  },
  
  COASTAL: {
    value: 'COASTAL',
    label: 'Coastal/Littoral',
    description: 'Beach, port areas, amphibious operations',
    icon: '🏖️',
    
    time_modifier: 1.1,
    distance_modifier: 1.05,
    helicopter_factor: 0.95,      // Wind can be issue
    max_speed_kmh: 50,
    night_penalty: 1.2,
    
    speed_modifier: 0.80,         // Limited routes, coastal terrain
    availability_modifier: 0.80,  // Standard vehicles with route constraints
    
    typical_regions: ['Black Sea coast', 'Baltic coast', 'Mediterranean'],
    
    medical_considerations: {
      dust_dnbi_factor: 0.8,
      heatstroke_risk: 'MEDIUM',
      hypothermia_risk: 'HIGH',   // Water exposure
      drowning_risk: true,
    },
    
    educational_note: 'Naval assets may supplement evacuation. Beach LZs often available.'
  },
  
  ARCTIC: {
    value: 'ARCTIC',
    label: 'Arctic/Extreme Cold',
    description: 'Extreme cold, ice, limited daylight',
    icon: '❄️',
    
    time_modifier: 2.0,           // Extreme conditions
    distance_modifier: 1.3,
    helicopter_factor: 0.5,       // Icing, visibility
    max_speed_kmh: 30,
    night_penalty: 2.0,           // Polar night
    
    speed_modifier: 0.55,         // Deep snow, ice, severe mobility restrictions
    availability_modifier: 0.65,  // Cold-weather kit required, many vehicles non-operational
    
    
    typical_regions: ['Arctic Norway', 'Northern Finland', 'Alaska'],
    
    medical_considerations: {
      dust_dnbi_factor: 0.3,
      heatstroke_risk: 'NONE',
      hypothermia_risk: 'EXTREME',
      frostbite_risk: 'EXTREME',
      cold_weather_injury_multiplier: 3.0,
    },
    
    educational_note: 'Specialized cold weather evacuation training required. Heated transport essential.'
  }
};

// ============================================================================
// ROUTE TYPES
// ============================================================================

/**
 * Route types affecting evacuation
 */
export const ROUTE_TYPES = {
  PAVED_HIGHWAY: {
    value: 'PAVED_HIGHWAY',
    label: 'Paved Highway',
    speed_factor: 1.0,
    reliability: 0.95,
    armored_suitable: true
  },
  PAVED_SECONDARY: {
    value: 'PAVED_SECONDARY',
    label: 'Paved Secondary Road',
    speed_factor: 0.8,
    reliability: 0.90,
    armored_suitable: true
  },
  UNPAVED_IMPROVED: {
    value: 'UNPAVED_IMPROVED',
    label: 'Unpaved Improved (Gravel)',
    speed_factor: 0.6,
    reliability: 0.80,
    armored_suitable: true
  },
  UNPAVED_UNIMPROVED: {
    value: 'UNPAVED_UNIMPROVED',
    label: 'Unpaved Unimproved (Dirt)',
    speed_factor: 0.4,
    reliability: 0.60,
    armored_suitable: false  // Risk of getting stuck
  },
  TRAIL: {
    value: 'TRAIL',
    label: 'Trail/Path',
    speed_factor: 0.25,
    reliability: 0.40,
    armored_suitable: false
  },
  OFF_ROAD: {
    value: 'OFF_ROAD',
    label: 'Off-Road/Cross-Country',
    speed_factor: 0.2,
    reliability: 0.30,
    armored_suitable: false  // Only tracked vehicles
  }
};

// ============================================================================
// WEATHER CONDITIONS
// ============================================================================

/**
 * Weather modifiers for evacuation planning
 */
export const WEATHER_CONDITIONS = {
  CLEAR: {
    value: 'CLEAR',
    label: 'Clear',
    ground_modifier: 1.0,
    air_modifier: 1.0,
    visibility_km: 10
  },
  OVERCAST: {
    value: 'OVERCAST',
    label: 'Overcast',
    ground_modifier: 1.0,
    air_modifier: 0.95,
    visibility_km: 8
  },
  RAIN_LIGHT: {
    value: 'RAIN_LIGHT',
    label: 'Light Rain',
    ground_modifier: 1.1,
    air_modifier: 0.9,
    visibility_km: 5
  },
  RAIN_HEAVY: {
    value: 'RAIN_HEAVY',
    label: 'Heavy Rain',
    ground_modifier: 1.3,
    air_modifier: 0.6,
    visibility_km: 2
  },
  SNOW_LIGHT: {
    value: 'SNOW_LIGHT',
    label: 'Light Snow',
    ground_modifier: 1.2,
    air_modifier: 0.8,
    visibility_km: 4
  },
  SNOW_HEAVY: {
    value: 'SNOW_HEAVY',
    label: 'Heavy Snow/Blizzard',
    ground_modifier: 1.8,
    air_modifier: 0.2,
    visibility_km: 0.5
  },
  FOG: {
    value: 'FOG',
    label: 'Fog',
    ground_modifier: 1.2,
    air_modifier: 0.3,
    visibility_km: 0.2
  },
  SANDSTORM: {
    value: 'SANDSTORM',
    label: 'Sandstorm',
    ground_modifier: 1.5,
    air_modifier: 0.1,
    visibility_km: 0.1
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get terrain configuration by type
 * @param {string} terrainType - Terrain type key
 * @returns {Object} Terrain configuration or default (PLAINS)
 */
export const getTerrainFactor = (terrainType) => {
  return TERRAIN_TYPES[terrainType] || TERRAIN_TYPES.PLAINS;
};

/**
 * Get route type configuration
 * @param {string} routeType - Route type key
 * @returns {Object} Route configuration or default (PAVED_SECONDARY)
 */
export const getRouteType = (routeType) => {
  return ROUTE_TYPES[routeType] || ROUTE_TYPES.PAVED_SECONDARY;
};

/**
 * Get weather configuration
 * @param {string} weather - Weather condition key
 * @returns {Object} Weather configuration or default (CLEAR)
 */
export const getWeatherCondition = (weather) => {
  return WEATHER_CONDITIONS[weather] || WEATHER_CONDITIONS.CLEAR;
};

/**
 * Calculate effective distance considering terrain
 * @param {number} straightLineKm - Straight-line distance in km
 * @param {string} terrainType - Terrain type
 * @returns {number} Effective road distance in km
 */
export const calculateEffectiveDistance = (straightLineKm, terrainType) => {
  const terrain = getTerrainFactor(terrainType);
  return straightLineKm * terrain.distance_modifier;
};

/**
 * Calculate evacuation time for ground transport
 * @param {number} distanceKm - Distance in kilometers
 * @param {string} terrainType - Terrain type
 * @param {string} routeType - Route type
 * @param {string} weather - Weather condition
 * @param {boolean} isNight - Whether it's night operations
 * @returns {Object} Time calculation result
 */
export const calculateGroundEvacuationTime = (
  distanceKm,
  terrainType = 'PLAINS',
  routeType = 'PAVED_SECONDARY',
  weather = 'CLEAR',
  isNight = false
) => {
  const terrain = getTerrainFactor(terrainType);
  const route = getRouteType(routeType);
  const weatherMod = getWeatherCondition(weather);
  
  // Base speed from terrain
  let effectiveSpeed = terrain.max_speed_kmh * route.speed_factor;
  
  // Apply weather modifier
  effectiveSpeed *= weatherMod.ground_modifier;
  
  // Apply night penalty if applicable
  if (isNight) {
    effectiveSpeed /= terrain.night_penalty;
  }
  
  // Ensure minimum speed
  effectiveSpeed = Math.max(effectiveSpeed, 5); // Minimum 5 km/h
  
  // Calculate time
  const timeHours = distanceKm / effectiveSpeed;
  const timeMinutes = Math.round(timeHours * 60);
  
  // Golden hour check (60 minutes from injury to surgery)
  const goldenHourCompliant = timeMinutes <= 60;
  
  return {
    distance_km: distanceKm,
    effective_speed_kmh: Math.round(effectiveSpeed),
    time_hours: Math.round(timeHours * 100) / 100,
    time_minutes: timeMinutes,
    golden_hour_compliant: goldenHourCompliant,
    factors: {
      terrain: terrain.label,
      terrain_modifier: terrain.time_modifier,
      route: route.label,
      route_speed_factor: route.speed_factor,
      weather: weatherMod.label,
      weather_modifier: weatherMod.ground_modifier,
      night_operations: isNight,
      night_penalty: isNight ? terrain.night_penalty : 1.0
    }
  };
};

/**
 * Calculate helicopter evacuation time
 * @param {number} distanceKm - Distance in kilometers
 * @param {string} terrainType - Terrain type (affects LZ availability)
 * @param {string} weather - Weather condition
 * @param {number} cruiseSpeedKmh - Helicopter cruise speed (default 220 km/h)
 * @returns {Object} Time calculation result
 */
export const calculateHelicopterEvacuationTime = (
  distanceKm,
  terrainType = 'PLAINS',
  weather = 'CLEAR',
  cruiseSpeedKmh = 220
) => {
  const terrain = getTerrainFactor(terrainType);
  const weatherMod = getWeatherCondition(weather);
  
  // Check if helicopter operations are viable
  const heliViability = terrain.helicopter_factor * weatherMod.air_modifier;
  
  if (heliViability < 0.3) {
    return {
      viable: false,
      reason: `Helicopter operations limited (${Math.round(heliViability * 100)}% capability)`,
      terrain_factor: terrain.helicopter_factor,
      weather_factor: weatherMod.air_modifier
    };
  }
  
  // Add time for:
  // - Startup and liftoff: 5 min
  // - Flight time: distance / speed
  // - Landing and patient loading: 10 min
  // - Return flight: distance / speed (for round trip)
  
  const flightTimeMinutes = (distanceKm / cruiseSpeedKmh) * 60;
  const totalOneWay = 5 + flightTimeMinutes + 10; // Startup + flight + landing/loading
  
  return {
    viable: true,
    distance_km: distanceKm,
    cruise_speed_kmh: cruiseSpeedKmh,
    flight_time_minutes: Math.round(flightTimeMinutes),
    total_time_minutes: Math.round(totalOneWay),
    round_trip_minutes: Math.round(totalOneWay * 2 - 10), // Don't double-count loading at origin
    golden_hour_compliant: totalOneWay <= 60,
    capability_percent: Math.round(heliViability * 100),
    factors: {
      terrain: terrain.label,
      terrain_helicopter_factor: terrain.helicopter_factor,
      weather: weatherMod.label,
      weather_air_modifier: weatherMod.air_modifier
    }
  };
};

/**
 * Get default distance based on facility roles
 * Uses NATO planning factors
 * @param {string} fromRole - Source facility role (ROLE1, ROLE2_BASIC, etc.)
 * @param {string} toRole - Destination facility role
 * @returns {Object} Default distance configuration
 */
export const getDefaultDistance = (fromRole, toRole) => {
  const distanceMatrix = {
    'ROLE1_to_ROLE2_BASIC': {
      default_km: 15,
      min_km: 5,
      max_km: 30,
      typical_terrain: 'ROLLING_HILLS',
      typical_route: 'UNPAVED_IMPROVED'
    },
    'ROLE2_BASIC_to_ROLE2_ENHANCED': {
      default_km: 30,
      min_km: 15,
      max_km: 60,
      typical_terrain: 'ROLLING_HILLS',
      typical_route: 'PAVED_SECONDARY'
    },
    'ROLE2_ENHANCED_to_ROLE3': {
      default_km: 80,
      min_km: 40,
      max_km: 150,
      typical_terrain: 'PLAINS',
      typical_route: 'PAVED_HIGHWAY'
    },
    'ROLE3_to_ROLE4': {
      default_km: 300,
      min_km: 100,
      max_km: 500,
      typical_terrain: 'PLAINS',
      typical_route: 'PAVED_HIGHWAY'
    }
  };
  
  const key = `${fromRole}_to_${toRole}`;
  return distanceMatrix[key] || {
    default_km: 20,
    min_km: 5,
    max_km: 100,
    typical_terrain: 'PLAINS',
    typical_route: 'PAVED_SECONDARY'
  };
};

/**
 * Get terrain options for UI dropdown
 * @returns {Array} Array of {value, label} options
 */
export const getTerrainOptions = () => {
  return Object.entries(TERRAIN_TYPES).map(([key, val]) => ({
    value: key,
    label: `${val.icon} ${val.label}`,
    description: val.description
  }));
};

/**
 * Get route type options for UI dropdown
 * @returns {Array} Array of {value, label} options
 */
export const getRouteOptions = () => {
  return Object.entries(ROUTE_TYPES).map(([key, val]) => ({
    value: key,
    label: val.label,
    speed_factor: val.speed_factor
  }));
};

/**
 * Get weather options for UI dropdown
 * @returns {Array} Array of {value, label} options
 */
export const getWeatherOptions = () => {
  return Object.entries(WEATHER_CONDITIONS).map(([key, val]) => ({
    value: key,
    label: val.label,
    ground_modifier: val.ground_modifier,
    air_modifier: val.air_modifier
  }));
};

/**
 * Validate distance configuration
 * @param {Object} config - Distance configuration object
 * @returns {Object} Validation result with errors/warnings
 */
export const validateDistanceConfig = (config) => {
  const errors = [];
  const warnings = [];
  
  if (!config.distance_km || config.distance_km <= 0) {
    errors.push('Distance must be greater than 0');
  }
  
  if (config.distance_km > 500) {
    warnings.push('Distance exceeds typical strategic evacuation range');
  }
  
  if (!config.terrain_type || !TERRAIN_TYPES[config.terrain_type]) {
    errors.push('Invalid terrain type');
  }
  
  // Check golden hour compliance
  if (config.estimated_time_min && config.estimated_time_min > 60) {
    warnings.push(`Evacuation time (${config.estimated_time_min} min) exceeds Golden Hour`);
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const terrainFactorsConstants = {
  TERRAIN_TYPES,
  ROUTE_TYPES,
  WEATHER_CONDITIONS,
  getTerrainFactor,
  getRouteType,
  getWeatherCondition,
  calculateEffectiveDistance,
  calculateGroundEvacuationTime,
  calculateHelicopterEvacuationTime,
  getDefaultDistance,
  getTerrainOptions,
  getRouteOptions,
  getWeatherOptions,
  validateDistanceConfig
};

export default terrainFactorsConstants;
